import { useState, useEffect, useCallback } from "react";

interface EventRow {
  id: string;
  eventType: string;
  entityId: string | null;
  entityType: string | null;
  actorName: string | null;
  nodeId: string | null;
  detail: string | null;
  description: string | null;
  createdAt: string;
}

const EVENT_TYPES = [
  "fudge_created", "fudge_reinstated", "fudge_reinstated_manual", "fudge_reapplied",
  "tag_printed", "tag_purged", "tag_reused", "tag_scan_expired",
  "user_login", "sync_completed", "db_backup", "db_restore",
];

const TYPE_COLORS: Record<string, string> = {
  fudge_created:            "bg-green-100 text-green-700",
  fudge_reinstated:         "bg-blue-100 text-blue-700",
  fudge_reinstated_manual:  "bg-amber-100 text-amber-700",
  fudge_reapplied:          "bg-purple-100 text-purple-700",
  tag_printed:              "bg-slate-100 text-slate-600",
  tag_purged:               "bg-red-100 text-red-600",
  tag_reused:               "bg-orange-100 text-orange-600",
  tag_scan_expired:         "bg-red-50 text-red-400",
  user_login:               "bg-slate-100 text-slate-500",
  sync_completed:           "bg-slate-100 text-slate-500",
  db_backup:                "bg-indigo-100 text-indigo-600",
  db_restore:               "bg-indigo-100 text-indigo-700",
};

export default function EventsPage() {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [typeFilter, setTypeFilter] = useState("");
  const [fromFilter, setFromFilter] = useState("");
  const [toFilter, setToFilter] = useState("");
  const [nodeFilter, setNodeFilter] = useState("");

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (typeFilter) p.set("type", typeFilter);
    if (fromFilter) p.set("from", fromFilter);
    if (toFilter)   p.set("to", toFilter);
    if (nodeFilter) p.set("node", nodeFilter);
    return p.toString();
  }, [typeFilter, fromFilter, toFilter, nodeFilter]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const qs = buildParams();
    const r = await fetch(`/api/events${qs ? "?" + qs : ""}`);
    if (r.ok) setEvents(await r.json());
    setLoading(false);
  }, [buildParams]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  function handleExport() {
    const qs = buildParams();
    window.location.href = `/api/events/export${qs ? "?" + qs : ""}`;
  }

  function fmt(iso: string) {
    return new Date(iso).toLocaleString();
  }

  function parseDetail(raw: string | null, description: string | null): string {
    const parts: string[] = [];
    if (description) parts.push(description);
    if (raw) {
      try { parts.push(JSON.stringify(JSON.parse(raw), null, 0).slice(0, 60)); }
      catch { parts.push(raw.slice(0, 60)); }
    }
    return parts.join(" — ");
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Event Log</h1>
          <p className="text-sm text-slate-500 mt-0.5">Append-only audit trail — synchronised across all nodes</p>
        </div>
        <button onClick={handleExport}
          className="px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-md hover:bg-slate-800">
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Event Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All types</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
          <input type="date" value={fromFilter} onChange={(e) => setFromFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
          <input type="date" value={toFilter} onChange={(e) => setToFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Node</label>
          <input type="text" value={nodeFilter} onChange={(e) => setNodeFilter(e.target.value)}
            placeholder="e.g. Pi-Site-A"
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-36" />
        </div>
        <button onClick={fetchEvents}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
          Apply
        </button>
        <button onClick={() => { setTypeFilter(""); setFromFilter(""); setToFilter(""); setNodeFilter(""); }}
          className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50">
          Clear
        </button>
      </div>

      {/* Event table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-slate-400">Loading…</p>
        ) : events.length === 0 ? (
          <p className="p-6 text-sm text-slate-400">No events found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3">Date / Time</th>
                <th className="text-left px-4 py-3">Event</th>
                <th className="text-left px-4 py-3">Actor</th>
                <th className="text-left px-4 py-3">Node</th>
                <th className="text-left px-4 py-3">Entity</th>
                <th className="text-left px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-400 font-mono whitespace-nowrap">
                    {fmt(e.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_COLORS[e.eventType] ?? "bg-slate-100 text-slate-500"}`}>
                      {e.eventType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{e.actorName ?? "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{e.nodeId ?? "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-400">
                    {e.entityType && e.entityId ? `${e.entityType}/${e.entityId.slice(0, 8)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 max-w-xs truncate"
                    title={[e.description, e.detail].filter(Boolean).join(" — ")}>
                    {parseDetail(e.detail, e.description)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {events.length === 1000 && (
          <p className="px-4 py-3 text-xs text-slate-400 border-t border-slate-100">
            Showing first 1,000 results — use filters or export CSV to see more.
          </p>
        )}
      </div>
    </div>
  );
}
